<!-- <div *ngIf="loading" class="text-center py-5">
  <div class="spinner-border text-primary"></div>
  <p class="mt-2">{{ 'widget.reservation.booking.loadingAvailability' | translate }}</p>
</div> -->

<div *ngIf="loading" style="height: 40px">
  <mat-progress-spinner [diameter]="20" color="primary" mode="indeterminate"> </mat-progress-spinner>
</div>

<ng-container *ngIf="!loading && config">
  <ng-container *ngIf="tables?.length && allTimes?.length; else noDataTpl">
    <div class="booking-wrapper">
      <!-- ── Legend ──────────────────────────────────────────────────────── -->
      <div class="bw-legend">
        <span class="bw-legend-item">
          <span class="bw-dot bw-dot-available"></span>{{ 'widget.reservation.booking.available' | translate }}
        </span>
        <span class="bw-legend-item">
          <span class="bw-dot bw-dot-selected"></span>{{ 'widget.reservation.booking.selected' | translate }}
        </span>
        <span class="bw-legend-item">
          <span class="bw-dot bw-dot-hover"></span>{{ 'widget.reservation.booking.preview' | translate }}
        </span>
        <span class="bw-legend-item">
          <span class="bw-dot bw-dot-booked"></span>{{ 'widget.reservation.booking.notAvailable' | translate }}
        </span>
      </div>

      <!-- ── Inline error message ─────────────────────────────────────────── -->
      <div *ngIf="errorMsg" class="bw-error-banner">
        <span class="bw-error-icon">⚠</span>
        <span class="bw-error-text">{{ errorMsg }}</span>
        <button type="button" mat-flat-button color="primary" class="bw-error-close" (click)="dismissError()">✕</button>
      </div>

      <!-- ── Grid ─────────────────────────────────────────────────────────── -->
      <div class="bw-grid-card">
        <div class="bw-scroll">
          <table class="bw-table">
            <thead>
              <tr>
                <th class="bw-th-time">{{ 'widget.reservation.booking.time' | translate }}</th>
                <th *ngFor="let table of tables" class="bw-th-lane" [title]="table.room?.name || ''">
                  {{ table.name }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr *ngFor="let time of allTimes">
                <td class="bw-time-cell" [class.bw-time-unavailable]="!availableTimeMap[time]">
                  {{ formatTime(time) }}
                </td>
                <td
                  *ngFor="let table of tables"
                  class="bw-slot"
                  [ngClass]="getCellClasses(table.id, time)"
                  [title]="getTitle(table.id, table.name, time)"
                  (mouseenter)="onMouseEnter(table.id, time)"
                  (mouseleave)="onMouseLeave()"
                  (click)="handleCellClick(table.id, table.name, time)"
                >
                  <!-- Selected fill: colored region + border, sized to the real (offset-aware) overlap for this row -->
                  <div
                    *ngIf="isInSelectedBlock(table.id, time)"
                    class="bw-selected-fill"
                    [ngClass]="getSelectedFillClasses(table.id, time)"
                    [ngStyle]="getSelectedFillStyle(table.id, time)"
                  ></div>

                  <!-- Hover preview fill: same idea, only shown while hovering a valid target -->
                  <div
                    *ngIf="
                      isInHoverBlock(table.id, time) &&
                      isHoverBlockValid(table.id) &&
                      !isInSelectedBlock(table.id, time)
                    "
                    class="bw-hover-fill"
                    [ngClass]="getHoverFillClasses(table.id, time)"
                    [ngStyle]="getHoverFillStyle(table.id, time)"
                  ></div>

                  <!-- Selected time label — rendered on whichever row sits at the block's temporal center -->
                  <div
                    *ngIf="isLabelRow(table.id, time)"
                    class="bw-block-label bw-label-selected"
                    [ngStyle]="getLabelStyle(table.id, time)"
                  >
                    <div class="bw-time-label">
                      <div>
                        {{ formatTime(getActualStartTime(table.id, getSelectedBlockStartRow(table.id, time))) }}
                      </div>
                      <div class="bw-time-separator">–</div>
                      <div>{{ formatTime(calcEndTime(getSelectedBlockStartRow(table.id, time), table.id)) }}</div>
                    </div>
                  </div>

                  <!-- Hover time label — same center-row logic, only while previewing a valid target -->
                  <div
                    *ngIf="
                      isHoverLabelRow(table.id, time) &&
                      isHoverBlockValid(table.id) &&
                      !isInSelectedBlock(table.id, time)
                    "
                    class="bw-block-label bw-label-hover"
                    [ngStyle]="getHoverLabelStyle(table.id, time)"
                  >
                    <div class="bw-time-label">
                      <div>{{ formatTime(getActualStartTime(table.id, hoveredBlockStart)) }}</div>
                      <div class="bw-time-separator">–</div>
                      <div>{{ formatTime(calcEndTime(hoveredBlockStart, table.id)) }}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Selection Summary ─────────────────────────────────────────────── -->
      <div *ngIf="hasSelection()" class="bw-summary">
        <div class="bw-summary-header">
          <span class="bw-summary-title">{{ 'widget.reservation.booking.yourSelection' | translate }}</span>
          <button type="button" mat-flat-button color="primary" class="bw-btn-remove" (click)="clearAll()">
            {{ 'widget.reservation.booking.removeAll' | translate }}
          </button>
        </div>

        <ul class="bw-summary-list">
          <li *ngFor="let s of selections" class="bw-summary-row">
            <div class="bw-summary-left">
              <span class="bw-summary-table">{{ s.tableName }}</span>
              <span class="bw-summary-time">{{ formatTime(s.startTime) }} – {{ formatTime(s.endTime) }}</span>
            </div>
            <div class="bw-summary-right">
              <span class="bw-summary-price">{{ currencySymbol }}{{ formatPrice(pricePerBooking) }}</span>
              <button
                type="button"
                mat-flat-button
                color="primary"
                class="bw-btn-remove"
                (click)="removeSelection(s.tableId, s.rowKey)"
                [title]="'widget.reservation.booking.remove' | translate"
              >
                <i class="far fa-trash-alt"></i>
              </button>
            </div>
          </li>
        </ul>

        <div class="bw-summary-footer">
          <span class="bw-summary-count"
            >{{ selections.length }} × {{ currencySymbol }}{{ formatPrice(pricePerBooking) }}</span
          >
          <span class="bw-summary-total">
            {{ 'widget.reservation.booking.total' | translate }}:
            <strong>{{ currencySymbol }}{{ getGrandTotal() }}</strong></span
          >
        </div>
      </div>

      <!-- ── Empty state ───────────────────────────────────────────────────── -->
      <div *ngIf="!hasSelection()" class="bw-empty">
        <p class="bw-empty-main">
          {{
            'widget.reservation.booking.clickSlotToBook'
              | translate
                : {
                    tableLabel: shift?.tableLabel?.amountLabelSingle || ('tablePlan.table' | translate)
                  }
          }}
        </p>
        <p class="bw-empty-sub">
          {{ 'widget.reservation.booking.maxTables' | translate: { max: maxSelections, tableLabel: tableLabel } }}
        </p>
      </div>
    </div>
  </ng-container>

  <ng-template #noDataTpl>
    <div class="bw-no-data">{{ 'widget.reservation.booking.noAvailabilityLoaded' | translate }}</div>
  </ng-template>
</ng-container>
